import { useContext, useRef } from "react";
import { useEffect } from "react";
import { useState } from "react";
import { MdPause, MdPlayArrow, MdSkipNext } from "react-icons/md";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import UserContext from "../../../hooks/UserContext";
import theme from "../../../UI/theme";
import MusicPlayer from "../../musicPlayer/MusicPlayer";
import playFromTypes from "../../musicPlayer/playFromTypes";
import PanelButton from "../PanelButton";
import MusicPlayerSkeleton from "../skeletons/MusicPlayerSkeleton";
import PlayFromModal from "./PlayFromModal";
import { hasVenuePermission, permissionTypes } from "../../permission/permissions";
import { useSubscription } from "../../../hooks/useSubscription";
import { subscriptionsTypes } from "../../../scripts/subscription/subscriptionTypes";
import VirtualDjPlayerItem from "./VirtualDj/VirtualDjPlayerItem";
import Button from "../../UiComponents/Button";

let musicPlayerObject;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border: 4px solid ${theme.colors.darkSpace80};
` 

const PlayerContainer = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: 50px 50px;
    align-items: center;
    gap: 10px;
    overflow: hidden;
    border: none;
    border-radius: 2px;
    box-sizing: border-box;
    background-color: ${theme.colors.darkSpace};
    padding: 10px;
    height: fit-content;
    width: 100%;

    @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
        grid-template-rows: 40px 40px;
    }
`

const AlbumCover = styled.img`
    height: 110px;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 2px;
    grid-row: 1 / span 2;

    @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
        height: 90px;
    }
`

const AlbumPlaceHolder = styled.div`
    height: 110px;
    aspect-ratio: 1;
    border-radius: 2px;
    grid-row: 1 / span 2;
    background-color: ${theme.colors.darkSpace90};

    @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
        height: 90px;
    }
`

const TopInfoWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0;
    margin: 0;

    min-width: 0;
    width: 100%;
`

const InfoContainer = styled.div`
    height: 100%;
    display: flex;
    align-items: start;
    justify-content: start;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
`

const RightInfoContainer = styled(InfoContainer)`
  align-items: flex-end;   /* move children to the right edge */
  text-align: right;       /* align the text itself to the right */
`;

const InfoText = styled.label`
    display: block;
    max-width: 100%;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    font-size: ${theme.fontSize.larger};
    text-overflow: ellipsis;
`

const ArtistText = styled(InfoText)`
    color: ${theme.colors.darkSpace50};
    font-weight: 100;
    font-size: ${theme.fontSize.small};
`

const ControlContainer = styled.div`
    height: 100%;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 50px 50px;
    gap: 10px;
    align-items: end;
    justify-content: end;
    overflow: hidden;

    @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
        grid-template-columns: 1fr 40px 40px;
    }
`

const PlayFromButton = styled(PanelButton)`
    align-items: start;
    width: fill-available;
`

const SleepingContainer = styled.div`
    height: 100%;
    grid-column: 1/-1;
    grid-row: 1/-1;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
`

const loadingStates = {
    NONE: 'NONE',
    LOADING: 'LOADING',
    READY: 'READY',
    FAILED: 'FAILED',
}

const playStates = {
    PLAYING: 'PLAYING',
    PAUSED: 'PAUSED'
}

const VolumeWarning = styled.label`
    color: ${theme.colors.raspberryRed};
    grid-column: 1 / -1;
`

const MusicPlayerItem = props => {
    const [musicPlayerState, setMusicPlayerState] = useState(loadingStates.NONE);
    const [playState, setPlayState] = useState();
    const [nextSongState, setNextSongState] = useState();
    const [showPlayFromModal, setShowPlayFromModal] = useState(false);
    const [music, setMusic] = useState();
    const userContext = useContext(UserContext);
    const history = useHistory();
    let musicPlayer = useRef(musicPlayerObject);
    const [playlistsInDataBase, setPlaylistsInDataBase] = useSubscription(subscriptionsTypes.playlists, props.musicPlayer?.musicPlayerId);
    const [currentBestMatchPlaylist, setCurrentBestMatchPlaylist] = useState();
    const [hasRecommendation, setHasRecommendation] = useState(false);
    const [isWrongVolume, setIsWrongVolume] = useState(musicPlayer?.current?.defaultVolume && music?.volume && (music?.volume !== musicPlayer?.current?.defaultVolume));
    const [missingDefaultVolume, setMissingDefaultVolume] = useState(musicPlayer?.current?.defaultVolume === undefined || musicPlayer?.current?.defaultVolume === null);

    useEffect(() => {
        if (Array.isArray(playlistsInDataBase) && playlistsInDataBase.length > 0) {
            musicPlayer.current?.updateAvailablePlaylists(playlistsInDataBase);
            let bestMatch = getBestMatchingPlaylist(playlistsInDataBase);
            setCurrentBestMatchPlaylist(bestMatch);
        }
    }, [playlistsInDataBase]);

    useEffect(() => {
        if (musicPlayer.current && music) {
            const bestMatch = getBestMatchingPlaylist(musicPlayer.current?.music?.availablePlaylists || []);
            setCurrentBestMatchPlaylist(bestMatch);
        }
    }, [music]);

    useEffect(() => {
        setIsWrongVolume(musicPlayer?.current?.defaultVolume && music?.volume && (music?.volume !== musicPlayer?.current?.defaultVolume));
        setMissingDefaultVolume(musicPlayer?.current?.defaultVolume === undefined || musicPlayer?.current?.defaultVolume === null);
    }, [music?.volume, musicPlayer?.current?.defaultVolume]);

    const getBestMatchingPlaylist = (availablePlaylists) => {
        const sortedPlaylists = [...availablePlaylists].sort((a, b) => b.moodMatch - a.moodMatch);
        const highestMoodMatch = sortedPlaylists?.[0]?.moodMatch || 0;
        const candidates = sortedPlaylists.filter(playlist => playlist.moodMatch === highestMoodMatch);

        const currentlyPlayingId = music?.playFrom?.id;
        if (!currentlyPlayingId) return {};
        const currentIsTopCandidate = candidates.some(p => p.remotePlaylistId === currentlyPlayingId);

        const selectedPlaylist = currentIsTopCandidate
            ? candidates.find(p => p.remotePlaylistId === currentlyPlayingId)
            : candidates[Math.floor(Math.random() * candidates.length)];

        if (selectedPlaylist) setRecommendation(selectedPlaylist);
        return selectedPlaylist;
    }

    const setRecommendation = (bestMatch) => {
        if (!bestMatch || !music?.playFrom?.id || !bestMatch?.remotePlaylistId) {
            setHasRecommendation(false);
            return;
        }
        if (music?.playFrom?.id !== bestMatch?.remotePlaylistId) {
            setHasRecommendation(true);
        } else {
            setHasRecommendation(false);
        }
    }

    const onMusicChange = (newMusicObject) => {
        const newMusic = JSON.parse(JSON.stringify(newMusicObject));
        setMusic(prev => {
            setMusicPlayerState(loadingStates.READY);
            if (newMusic?.state === playStates.PLAYING) {
                setPlayState(playStates.PLAYING);
            } else if (newMusic?.state === playStates.PAUSED) {
                setPlayState(playStates.PAUSED);
            }

            if ((!nextSongState && newMusic?.songTitle) || newMusic?.songTitle !== music?.songTitle) {
                setNextSongState(loadingStates.READY);
            }

            if (props.onMusicChange) {
                props.onMusicChange(newMusic);
            }

            return newMusic;
        });
    }

    const changePlayState = () => {
        if (!hasVenuePermission(userContext, permissionTypes.panelViewWrite)) {
            return;
        }
        const oldPlayState = playState;
        let result;
        if (playState === playStates.PLAYING) {
            result = musicPlayer.current?.pause();
            setPlayState(playStates.PAUSED);
        } else if (playState === playStates.PAUSED || musicPlayer?.current?.isConnected === false) {
            result = musicPlayer.current?.play();
            setPlayState(playStates.PLAYING);
        }

        if (result === false) {
            setPlayState(oldPlayState);
        }
    }

    const wakeupPlayer = () => {
        if (!hasVenuePermission(userContext, permissionTypes.panelViewWrite)) {
            return;
        }
        musicPlayer?.current?.wakeupPlayer();
    }

    const onNextSong = () => {
        if (!hasVenuePermission(userContext, permissionTypes.panelViewWrite)) {
            return;
        }
        setNextSongState(loadingStates.LOADING);
        musicPlayer.current?.nextSong();
    }

    useEffect(() => {
        setMusicPlayerState(loadingStates.LOADING);
        musicPlayer.current = new MusicPlayer(props.musicPlayer, onMusicChange);
        musicPlayer.current?.init()?.then(() => {
            setMusicPlayerState(loadingStates.READY);
            props.onAuthorizationChange?.(
                props.musicPlayer?.musicPlayerId,
                musicPlayer.current?.isAuthorized
            );
        });

        return () => {
            musicPlayer.current.cleanup();
        }
    }, []);

    return <>
        {musicPlayerState === loadingStates.READY ? <>
            <Container>
            {!props.inSettingsView && 
                <VirtualDjPlayerItem
                    musicPlayer={musicPlayer}
                    music={music}
                    userContext={userContext}
                    currentBestMatchPlaylist={currentBestMatchPlaylist}
                    hasRecommendation={hasRecommendation}
                    setShowPlayFromModal={setShowPlayFromModal}
                    hubs={props.hubs}
                />
            }
            <PlayerContainer>
                
                {musicPlayer?.current?.isConnected ? <>
                    {music?.imageUrl ?
                        <AlbumCover src={music?.imageUrl} /> :
                        <AlbumPlaceHolder />
                    }
                    <TopInfoWrapper>
                        <InfoContainer>
                            {music?.playFrom?.type?.key === playFromTypes.NULL.key ? <>
                                <InfoText>No music selected</InfoText>
                            </> : <>
                                <InfoText>{musicPlayer?.current?.isConnected === true ? (music?.songTitle ?? 'Title unavailable') : 'Music player not connected'}</InfoText>
                                <ArtistText>{musicPlayer?.current?.isConnected === true ? (music?.artists?.[0] ?? 'Artist unavailable') : 'Press play to wake up'}</ArtistText>
                            </>}
                        </InfoContainer>
                    </TopInfoWrapper>
                    <ControlContainer>
                        <PlayFromButton onClick={() => setShowPlayFromModal(true)}>
                            {music?.playFrom?.type?.key === playFromTypes.NULL.key ? <>
                                <label>Select music</label>
                                <label></label>
                            </> : <>
                                <label>{playFromTypes[music?.playFrom?.type]?.display}</label>
                                <label>{music?.playFrom?.name}</label>
                            </>}
                        </PlayFromButton>

                        <PanelButton onClick={() => changePlayState()}>
                            {playState === playStates.PLAYING ? <>
                                <MdPause /><label>Pause</label>
                            </> : <>
                                <MdPlayArrow /><label>Play</label>
                            </>}
                        </PanelButton>

                        <PanelButton disabled={nextSongState === loadingStates.LOADING} onClick={() => onNextSong()}><MdSkipNext /><label>Next</label></PanelButton>
                    </ControlContainer>
                </> :
                    <SleepingContainer>
                        <label>
                            {props.musicPlayer?.name} - {musicPlayer?.current?.isAuthorized === false
                                ? 'Soundtrack authorization expired'
                                : 'Player is currently offline'}
                        </label>
                        {musicPlayer?.current?.isAuthorized === false &&
                            !props.inSettingsView &&
                            hasVenuePermission(userContext, permissionTypes.settingsRead) ?
                            <Button secondary small onClick={() => history.push('/settings/musicPlayers')}>
                                Reauthenticate in settings
                            </Button>
                            : <></>}
                    </SleepingContainer>}
                    { isWrongVolume &&
                        <VolumeWarning>
                            Your Music Player is set to the wrong volume, please set it to {musicPlayer?.current?.defaultVolume} in the Soundtrack App.
                        </VolumeWarning>
                    }      
                    { missingDefaultVolume &&
                        <VolumeWarning style={{color: theme.colors.whiteSpace}}>
                            Missing default volume. Please set a default volume under settings.
                        </VolumeWarning>
                    }              
            </PlayerContainer>
            </Container>
        </> : <></>}

        <PlayFromModal
            show={showPlayFromModal === true}
            close={() => { setShowPlayFromModal(false) }}
            musicPlayer={musicPlayer.current}
            userContext={userContext}
            bestMatchPlaylist={currentBestMatchPlaylist}
        />

        {musicPlayerState === loadingStates.LOADING ? <>
            <MusicPlayerSkeleton />
        </> : <></>}

    </>
}

export default MusicPlayerItem;
